import { Fragment, useState } from 'react';
import DnaIdentificationsPanel, {
  LatestIdentification, confidenceText, levelText, ncbiUrl, shortName, theCall,
} from '../../dna/DnaIdentificationsPanel';
import { resolveLinkedRecordNav } from '../../../utils/linkedRecordNav';
import { resolvePhotoUrl } from '../../../utils/photoUrl';
import { displayName } from '../../../utils/userLabel';
import IsolatePhylogeny from '../IsolatePhylogeny';
import SequenceViewer from '../../dna/SequenceViewer';
import GeneticConclusion from '../../dna/GeneticConclusion';
import { canDo } from '../stages';
import { Code, Day, useDnaT } from '../cells';
import * as S from '../../specimens/styles';

/*
 * Genetic identification (Home's card, once "DNA extraction"; 2026-10-19):
 * each extract taken from the isolate's cultures as it was
 * recorded (when, where, by whom, the kit and its lot, concentration and
 * purity, the tube's place), its sequencing runs with everything the run
 * form took (platform and barcode, primers, provider, length, coverage,
 * quality, level and confidence, the result text, the sequence files), what
 * sequence analysis concluded for each marker (PITAX's current answers, with
 * the best match), and the reference phylogeny opened on the isolate's genus
 * (IsolatePhylogeny). DnaIdentificationsPanel -- the DNA page's own panel,
 * opened in place -- edits and withdraws the answers; an answer changed there
 * re-reads the row, its dossier and the hub's counts (onRefresh), as every
 * other write from the hub does.
 *
 * Since 0085, under each run its sequence -- the bases the run's file holds,
 * for the primer it was sequenced with (SequenceViewer, opened per run or all
 * at once) -- and under each extract the sequencing conclusion: the final
 * genetic identification a person reached after the analysis and the BLAST
 * query (GeneticConclusion).
 */
const blank = (v) => v === null || v === undefined || v === '';

export default function DnaTab({ item, dossier, user, t, onAction, onNavigate, onRefresh }) {
  const tDna = useDnaT();
  const [open, setOpen] = useState(null);
  const [sequences, setSequences] = useState({});          // run_id -> shown
  const extracts = dossier.dna || [];
  const toggleSequence = (runId) => setSequences((all) => ({ ...all, [runId]: !all[runId] }));
  const allRuns = extracts.flatMap((d) => d.runs || []);
  const allOpen = allRuns.length > 0 && allRuns.every((r) => sequences[r.run_id]);
  const showAll = () => setSequences(allOpen ? {} : Object.fromEntries(allRuns.map((r) => [r.run_id, true])));
  const answers = dossier.identifications?.dna || [];
  const canWrite = canDo(user, 'extractDna', item);
  const sourceCode = (d) => {
    if (d.plate_id) return (dossier.plates || []).find((p) => p.plate_id === d.plate_id)?.plate_code;
    if (d.slant_id) return (dossier.slants || []).find((s) => s.slant_id === d.slant_id)?.slant_code;
    return null;
  };
  const go = (table, id) => {
    const nav = resolveLinkedRecordNav(table, id);
    if (nav) onNavigate(nav.path, nav.state);
  };

  return (
    <div data-testid="hub-dna">
      {canWrite && ((dossier.plates || []).length > 0 || (dossier.slants || []).length > 0) && (
        <div className="hub-tab-actions">
          <button type="button" style={S.ghostBtn} onClick={() => onAction('extractDna', item)}
            title={t('tip.action.extractDna')}>{t('action.extractDna')}</button>
        </div>
      )}
      {extracts.length === 0 && <p style={S.hint}>{t('details.noDna')}</p>}
      {allRuns.length > 1 && (
        <div className="hub-tab-actions">
          <button type="button" style={smallBtn} aria-pressed={allOpen} onClick={showAll}
            data-testid="hub-dna-all-sequences" title={tDna('genetic.tip.allSequences')}>
            {allOpen ? tDna('genetic.hideAllSequences') : tDna('genetic.showAllSequences')}</button>
        </div>
      )}
      <ul className="hub-records">
        {extracts.map((d) => {
          const runs = d.runs || [];
          const mine = answers.filter((a) => a.dna_id === d.dna_id);
          return (
            <li key={d.dna_id} className="hub-record" data-testid={`hub-dna-${d.dna_id}`}>
              <div className="hub-record__head">
                <button type="button" className="hub-link" onClick={() => go('dna', d.dna_id)} title={t('tip.openRecord')}>
                  <Code strong>{d.isolate_code}</Code>
                </button>
                <span style={S.hint}>
                  <Day value={d.date_performed} />
                  {sourceCode(d) && <> · {t('extracts.from')} <Code>{sourceCode(d)}</Code></>}
                </span>
              </div>
              {d.latest_identification && (
                <div style={{ fontSize: 13 }}><LatestIdentification latest={d.latest_identification} t={tDna} /></div>
              )}
              <Extraction extract={d} t={t} tDna={tDna} />
              {runs.length > 0 && <Runs runs={runs} t={t} tDna={tDna} go={go} sequences={sequences}
                onSequence={toggleSequence} canEdit={canWrite} onChanged={() => onRefresh?.(item)} />}
              {mine.length > 0 && <Answers answers={mine} runs={runs} t={t} tDna={tDna} />}
              <GeneticConclusion extract={d} canEdit={canWrite} onSaved={() => onRefresh?.(item)} />
              <div className="hub-record__actions">
                {canDo(user, 'addRun', item) && (
                  <button type="button" style={smallBtn} onClick={() => onAction('addRun', item, { dnaId: d.dna_id })}
                    title={t('tip.action.addRun')} data-testid={`hub-add-run-${d.dna_id}`}>{t('action.addRun')}</button>
                )}
                <button type="button" style={smallBtn} aria-expanded={open === d.dna_id}
                  onClick={() => setOpen(open === d.dna_id ? null : d.dna_id)} title={t('tip.identifications')}>
                  {open === d.dna_id ? t('extracts.hideIdentifications') : t('extracts.showIdentifications')}
                </button>
              </div>
              {open === d.dna_id && (
                <DnaIdentificationsPanel dnaId={d.dna_id} isolateCode={d.isolate_code} canEdit={canWrite}
                  onClose={() => setOpen(null)} onChanged={() => onRefresh?.(item)} />
              )}
            </li>
          );
        })}
      </ul>
      <IsolatePhylogeny dossier={dossier} item={item} t={t} />
    </div>
  );
}

/** The extraction as it was recorded: only what was. */
function Extraction({ extract: d, t, tDna }) {
  const facts = [
    ['laboratory', tDna('isolateForm.laboratoryLabel'), d.laboratory],
    ['performedBy', tDna('isolateForm.performedByLabel'), d.performed_by_name && displayName(d.performed_by_name, t)],
    ['kit', tDna('isolateForm.extractionKitLabel'), d.extraction_kit],
    ['lot', tDna('isolateForm.extractionLotLabel'), d.extraction_lot],
    ['concentration', tDna('isolateForm.concentrationLabel'),
      blank(d.concentration_ng_ul) ? null : <bdi dir="ltr">{d.concentration_ng_ul}</bdi>],
    ['purity', tDna('isolateForm.a260280Label'), blank(d.a260_280) ? null : <bdi dir="ltr">{d.a260_280}</bdi>],
    ['place', t('extracts.place'), d.storage_location ? <Code>{d.storage_location}</Code> : null],
  ].filter(([, , value]) => !blank(value));
  return (
    <>
      {facts.length > 0 && (
        <dl style={factList} data-testid={`hub-dna-extraction-${d.dna_id}`}>
          {facts.map(([key, label, value]) => (
            <div key={key} style={{ display: 'contents' }}>
              <dt style={dt}>{label}</dt>
              <dd style={dd} data-field={key}>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {d.notes && <p style={{ fontSize: 13, margin: '4px 0 0' }}>{d.notes}</p>}
    </>
  );
}


/** Every run, as the DNA page's run form recorded it. */
function Runs({ runs, t, tDna, go, sequences = {}, onSequence, canEdit, onChanged }) {
  return (
    <div className="table-wrap">
      <table style={{ borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }} data-testid="hub-dna-runs">
        <thead>
          <tr>
            {['run', 'gene', 'platform', 'primers', 'sent', 'length', 'coverage', 'quality', 'level', 'files'].map((c) => (
              <th key={c} scope="col" style={th}>{t(`extracts.columns.${c}`)}</th>
            ))}
            <th scope="col" style={th}>{tDna('genetic.sequence')}</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const primers = [r.amplification_primers_name, r.sequencing_primers_name].filter(Boolean);
            const level = [r.identification_level_name, r.identification_confidence_name].filter(Boolean).join(' · ');
            return (
              <Fragment key={r.run_id}>
                <tr data-testid={`hub-dna-run-${r.run_id}`}>
                  <td style={tdc}>
                    <button type="button" className="hub-link" onClick={() => go('dna_sequencing_runs', r.run_id)}
                      title={t('tip.openRecord')}><Code>{r.run_code}</Code></button>
                  </td>
                  <td style={tdc}>{r.gene_name || '—'}</td>
                  <td style={tdc}>
                    {r.platform ? tDna(`platforms.${r.platform}`, { defaultValue: r.platform }) : '—'}
                    {r.barcode ? <> · <Code>{r.barcode}</Code></> : null}
                  </td>
                  <td style={tdc}>
                    {primers.length ? <bdi dir="ltr">{primers.join(' / ')}</bdi> : '—'}
                    {r.sequencing_provider_name ? <div style={S.hint}>{r.sequencing_provider_name}</div> : null}
                  </td>
                  <td style={tdc}><Day value={r.date_sent_for_sequencing} /></td>
                  <td style={tdc}>{blank(r.sequence_length) ? '—' : <bdi dir="ltr">{r.sequence_length} bp</bdi>}</td>
                  <td style={tdc}>{blank(r.query_coverage) ? '—' : <bdi dir="ltr">{r.query_coverage}%</bdi>}</td>
                  <td style={tdc}>{r.sequence_quality_name || '—'}{r.needs_resequencing ? ` · ${t('extracts.resequence')}` : ''}</td>
                  <td style={tdc}>{level || '—'}</td>
                  <td style={tdc}>
                    {r.raw_sequence_file_url && (
                      <a href={resolvePhotoUrl(r.raw_sequence_file_url)} target="_blank" rel="noreferrer"
                        title={tDna('tip.openRawFile')}>{t('extracts.raw')}</a>
                    )}
                    {r.raw_sequence_file_url && r.processed_sequence_file_url ? ' · ' : null}
                    {r.processed_sequence_file_url && (
                      <a href={resolvePhotoUrl(r.processed_sequence_file_url)} target="_blank" rel="noreferrer"
                        title={tDna('tip.openProcessedFile')}>{t('extracts.processed')}</a>
                    )}
                    {!r.raw_sequence_file_url && !r.processed_sequence_file_url ? '—' : null}
                  </td>
                  <td style={tdc}>
                    <button type="button" style={smallBtn} aria-expanded={!!sequences[r.run_id]}
                      onClick={() => onSequence?.(r.run_id)} data-testid={`hub-dna-sequence-${r.run_id}`}
                      title={tDna('genetic.tip.show')}>
                      {sequences[r.run_id] ? tDna('genetic.hide') : tDna('genetic.show')}
                    </button>
                    {r.sequence_kept && <div style={S.hint}>{tDna('genetic.kept')}</div>}
                  </td>
                </tr>
                {sequences[r.run_id] && (
                  <tr>
                    <td colSpan={11} style={{ ...tdc, borderTop: 'none' }}>
                      <SequenceViewer runId={r.run_id} runCode={r.run_code} canEdit={canEdit} onChanged={onChanged} />
                    </td>
                  </tr>
                )}
                {(r.identification_blast_result || r.notes) && (
                  <tr>
                    <td colSpan={11} style={{ ...tdc, borderTop: 'none', paddingTop: 0 }}>
                      {r.identification_blast_result && (
                        <div data-testid={`hub-dna-run-result-${r.run_id}`}>
                          <span style={S.hint}>{tDna('table.blastResult')}: </span>
                          <bdi dir="ltr">{r.identification_blast_result}</bdi>
                        </div>
                      )}
                      {r.notes && <div style={S.hint}>{r.notes}</div>}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** What sequence analysis concluded for each marker: the answer that stands. */
function Answers({ answers, runs, t, tDna }) {
  const runCode = (id) => runs.find((r) => r.run_id === id)?.run_code;
  return (
    <div className="table-wrap">
      <table style={{ borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }} data-testid="hub-dna-answers">
        <caption style={{ textAlign: 'start', fontWeight: 600, fontSize: 13, padding: '2px 0' }}>
          {t('extracts.answers')}
        </caption>
        <thead>
          <tr>
            {[tDna('identifications.marker'), t('extracts.columns.call'), tDna('identifications.confidence'),
              tDna('identifications.bestMatch'), tDna('identifications.identity'), tDna('identifications.coverage'),
              t('extracts.columns.analysed')].map((label) => <th key={label} scope="col" style={th}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {answers.map((a) => {
            const call = theCall(a, tDna);
            const level = call.showLevel ? levelText(a.recommended_level, tDna) : '';
            const ncbi = ncbiUrl(a.best_match_accession);
            return (
              <tr key={a.identification_id} data-testid={`hub-dna-answer-${a.identification_id}`}>
                <td style={tdc}>
                  <Code>{a.marker}</Code>
                  {runCode(a.run_id) ? <div style={S.hint}><Code>{runCode(a.run_id)}</Code></div> : null}
                </td>
                <td style={tdc}>
                  {call.name ? <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{shortName(call.name)}</bdi> : call.text}
                  {level ? ` · ${level}` : ''}
                </td>
                <td style={tdc}>{confidenceText(a.confidence, tDna) || '—'}</td>
                <td style={tdc}>
                  {a.best_match_organism ? <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{a.best_match_organism}</bdi> : null}
                  {a.best_match_accession && (
                    <div>{ncbi
                      ? <a href={ncbi} target="_blank" rel="noreferrer" title={t('extracts.tip.accession')}><Code>{a.best_match_accession}</Code></a>
                      : <Code>{a.best_match_accession}</Code>}</div>
                  )}
                  {!a.best_match_organism && !a.best_match_accession ? '—' : null}
                </td>
                <td style={tdc}>{blank(a.identity_pct) ? '—' : <bdi dir="ltr">{a.identity_pct}%</bdi>}</td>
                <td style={tdc}>{blank(a.coverage_pct) ? '—' : <bdi dir="ltr">{a.coverage_pct}%</bdi>}</td>
                <td style={tdc}><Day value={a.analyzed_at || a.created_at} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const th = { padding: '4px 8px', textAlign: 'start', fontWeight: 600, whiteSpace: 'nowrap' };
const tdc = { padding: '4px 8px', borderTop: '1px solid var(--c-border)', verticalAlign: 'top' };
const smallBtn = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
const factList = { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 12px', fontSize: 13, margin: '6px 0 0' };
const dt = { color: 'var(--c-text-muted-fg)', margin: 0 };
const dd = { margin: 0 };
